import { useState } from "react";
import { Card, Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, DelButton, MoreButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import {
  getVendorSKUList2,
  getVendorSKUStatusCount,
  deactivateVendorSKUById,
} from "services/data.internal.service";
import { TableCard } from "ui-components/Layout";
import { activeVendorSKU } from "services/data.internal.service";
import { ActiveButton } from "ui-components/IconButton";
import { useTeamValue } from "providers/TeamContext";

function VendorSKUList() {
  const navigate = useNavigate();
  const {permissions, allowAll} = useTeamValue()
  const location = useLocation();
  const { customerCode, skuCode } = location.state;
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const [data, setData] = useState([]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["vendor-sku-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const addPermission = allowAll || (Boolean(permissions["Vendor SKU"]) && permissions["Vendor SKU"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["Vendor SKU"]) && permissions["Vendor SKU"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["Vendor SKU"]) && permissions["Vendor SKU"].findIndex(b => b==="deactivate") !== -1);
  const addRBPermission = allowAll || (Boolean(permissions["Retail Barcode"]) && permissions["Retail Barcode"].findIndex(b => b==="new") !== -1);
  
  const retailBarcodeAccess =
    allowAll ||
    (Boolean(permissions["Retail Barcode"]));
  const packTypeAccess =
    allowAll ||
    (Boolean(permissions["SKU Pack Type"]));


  useQuery({
    queryKey: ["vendor-sku-list", { customerCode, skuCode, active }],
    queryFn: () => getVendorSKUList2({ customerCode, skuCode, active }),
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["vendor-sku-list", "status"],
    queryFn: () => getVendorSKUStatusCount({ customerCode, skuCode }),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivateVendorSKUById, { onSuccess, onError });

  const activeMutation = useMutation(activeVendorSKU, { onSuccess, onError });

  const onEdit = (req) => navigate("/setup/sku/vendor-sku/edit", { state: req });

  const onDelete = (req) => {
    const { vendorSKU, ...data } = req;
    setDialog(
      <MessageDialog
        title={`Deactivate vendor SKU ${vendorSKU}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(data);
          setDialog(null);
        }}
      />
    );
  };

  const onActive = (req) => {
    const { vendorSKU, ...data } = req;
    setDialog(
      <MessageDialog
        title={`Activate vendor SKU ${vendorSKU}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(data);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Vendor SKU", accessorKey: "vendorSKU" },
    { header: "SKU Code", accessorKey: "skuCode" },
    { header: "Customer Code", accessorKey: "customerCode" },
    { header: "Vendor Code", accessorKey: "vendorCode" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { vendorSKU, skuCode, customerCode, vendorCode } = row.original;
        const state = { skuCode, customerCode, vendorCode };
        const optionsArray = []
        if (addRBPermission) optionsArray.push({
                    label: "Retail Barcode",
                    disabled: !addRBPermission,
                    onClick: () => navigate("/setup/sku/retail-barcode/add", { state }),
                  });
        if (packTypeAccess) optionsArray.push({
                    label: "SKU Pack Type",
                    onClick: () => navigate("/setup/sku/pack-type", { state }),
                  })
        if (active) {
          return (
            <>
              <EditButton title="edit vendor sku" disabled={!editPermission} onClick={() => onEdit(state)} />
              <DelButton
                title="deactivate vendor sku"
                disabled={!deactivatePermission}
                onClick={() => onDelete({ ...state, vendorSKU })}
              />
              <MoreButton
                options={optionsArray}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate vendor sku"
              disabled={!editPermission}
              onClick={() => onActive({ ...state, vendorSKU })}
            />
          );
        }
      },
    },
  ];
  const rows = data;
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" margin="10px">
          <MDButton
            variant="gradient"
            color="info"
            disabled={!addPermission}
            onClick={() => navigate("/setup/sku/vendor-sku/add",{ state: { skuCode } })}
          >
            <Icon>add</Icon>&nbsp;vendor sku
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={rows} />
      </TableCard>
    </ListLayout>
  );
}

export default VendorSKUList;
